import { useMemo } from 'react';
import {
  components,
  createFilter,
  FilterOptionOption,
  GroupBase,
  MenuListProps,
  OptionProps,
  SingleValueProps,
} from 'react-select';

import helm from '@/assets/ico/vendor/helm.svg?c';
import {
  PortainerSelect,
  Option,
} from '@/react/components/form-components/PortainerSelect';
import { FallbackImage } from '@/react/components/FallbackImage';

import { FormControl, Size } from '@@/form-components/FormControl';
import { Icon } from '@@/Icon';
import { Badge } from '@@/Badge';

import { Chart } from '../types';

import { RegistryValue } from './HelmRegistrySelect';
import { RegistryAlert } from './RegistryAlert';

const filterConfig = {
  // stringify the option to make the machine labels searchable
  stringify: (chart: FilterOptionOption<Option<Chart | null>>) => {
    if (!chart.data) {
      return '';
    }

    return `${chart.label} ${chart.data.value?.repo} ${chart.data.value?.annotations?.category}`;
  },
};

interface Props {
  charts?: Chart[];
  onSelect: (chart: Chart | null) => void;
  selectedChart: Chart | null;
  isLoadingCharts: boolean;
  selectedRegistry: RegistryValue | null;
  formError?: string;
  size?: Size;
  isRegistryError?: boolean;
  registryId?: number;
  isOCI?: boolean;
}

export function HelmTemplatesSelect({
  charts = [],
  onSelect,
  selectedChart,
  isLoadingCharts,
  selectedRegistry,
  formError,
  size = 'small',
  isRegistryError,
  registryId,
  isOCI,
}: Props) {
  const chartOptions = useMemo(
    () =>
      charts.map<Option<Chart>>((chart) => ({
        label: chart.name,
        value: chart,
      })),
    [charts]
  );

  return (
    <>
      <FormControl
        label="Helm chart"
        required
        errors={
          (!selectedRegistry &&
            'Please select a registry to load available Helm charts.') ||
          (!isLoadingCharts && formError) // showing the error while loading charts is not helpful
        }
        size={size}
      >
        <PortainerSelect<Chart | null>
          placeholder={getPlaceholderText()}
          value={selectedChart}
          options={chartOptions}
          isLoading={isLoadingCharts}
          menuPosition="fixed"
          onChange={(chart) => {
            onSelect(chart);
          }}
          components={{
            Option: ChartOption,
            SingleValue: ChartSingleValue,
            MenuList: ChartMenuList,
          }}
          data-cy="helm-templates-select"
          noOptionsMessage={() => 'No helm charts available.'}
          loadingMessage={() =>
            'Loading charts - initial download of Helm charts can take a few minutes...'
          }
          isClearable
          disabled={!selectedRegistry}
          filterOption={createFilter(filterConfig)}
        />
      </FormControl>
      {isRegistryError && isOCI && (
        <RegistryAlert registryId={registryId} isOCI={isOCI} />
      )}
    </>
  );

  function getPlaceholderText() {
    if (isLoadingCharts) {
      return 'Loading charts...';
    }
    if (selectedRegistry) {
      return `Select a Helm chart from ${selectedRegistry.name}`;
    }

    return 'No charts available';
  }
}

function ChartOption(
  props: OptionProps<
    Option<Chart | null>,
    false,
    GroupBase<Option<Chart | null>>
  >
) {
  const { data: option } = props;
  const chart = option.value;
  const category = chart?.annotations?.category;

  if (!chart) {
    return null;
  }

  return (
    <div>
      {/* eslint-disable-next-line react/jsx-props-no-spreading */}
      <components.Option {...props} className="truncate whitespace-nowrap">
        <div
          className="flex items-center gap-x-2 whitespace-nowrap text-inherit"
          title={chart.name}
        >
          <FallbackImage
            src={chart.icon}
            fallbackIcon={<Icon icon={helm} className="!h-6 !w-6" />}
            className="!h-6 !w-6"
            alt="Helm chart icon"
          />
          <div className="flex w-full items-center justify-between">
            <span className="truncate font-medium">{chart.name}</span>
            {category && <Badge type="muted">{category}</Badge>}
          </div>
        </div>
        {!!chart.description && (
          <div
            className="text-muted ml-8 overflow-hidden text-ellipsis whitespace-normal text-xs"
            title={chart.description}
            // truncate the description to 2 lines
            style={{
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
            }}
          >
            {chart.description}
          </div>
        )}
      </components.Option>
    </div>
  );
}

function ChartSingleValue(
  props: SingleValueProps<
    Option<Chart | null>,
    false,
    GroupBase<Option<Chart | null>>
  >
) {
  const { data: option } = props;
  const chart = option.value;

  if (!chart) {
    return null;
  }

  return (
    // eslint-disable-next-line react/jsx-props-no-spreading
    <components.SingleValue {...props}>
      <div className="flex items-center gap-x-2" title={chart.name}>
        <FallbackImage
          src={chart.icon}
          fallbackIcon={<Icon icon={helm} />}
          className="!h-4 !w-4"
          alt="Helm chart icon"
        />
        <span className="truncate">{chart.name}</span>
      </div>
    </components.SingleValue>
  );
}

// make the list bigger to have more space to browse through the charts
function ChartMenuList(props: MenuListProps<Option<Chart | null>>) {
  return (
    // eslint-disable-next-line react/jsx-props-no-spreading
    <components.MenuList {...props} className="!max-h-[400px]" />
  );
}
